import React, { useMemo, useRef, useState } from 'react';
import { 
  BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer,
  PieChart, Pie, Cell
} from 'recharts';
import { 
  X, Download, Users, Shield, HelpCircle, 
  MapPin, Home, TrendingUp, AlertCircle, FileText, Loader2
} from 'lucide-react';
import { Button } from './ui/button';
import jsPDF from 'jspdf';

const COLORS = ['#14b8a6', '#0891b2', '#6366f1', '#f59e0b', '#ef4444', '#8b5cf6', '#ec4899', '#10b981'];

const HC4CCReportModal = ({ isOpen, onClose, inquiries }) => {
  const [isGeneratingPDF, setIsGeneratingPDF] = useState(false);

  const reportData = useMemo(() => {
    if (!inquiries || inquiries.length === 0) return null;

    const statusCounts = {};
    const insuranceCounts = {};
    const coverageCounts = {};
    const metalCounts = {};
    const knowsServicesCounts = {};
    const helpfulInfoCounts = {};
    const wardCounts = {};
    const residenceCounts = {};

    inquiries.forEach(inq => {
      statusCounts[inq.status] = (statusCounts[inq.status] || 0) + 1;
      insuranceCounts[inq.insurance_company] = (insuranceCounts[inq.insurance_company] || 0) + 1;
      coverageCounts[inq.coverage_type] = (coverageCounts[inq.coverage_type] || 0) + 1;
      metalCounts[inq.metal_level] = (metalCounts[inq.metal_level] || 0) + 1;
      knowsServicesCounts[inq.knows_free_services] = (knowsServicesCounts[inq.knows_free_services] || 0) + 1;
      if (inq.ward) wardCounts[inq.ward] = (wardCounts[inq.ward] || 0) + 1;
      if (inq.residence) residenceCounts[inq.residence] = (residenceCounts[inq.residence] || 0) + 1;
      (inq.helpful_info || []).forEach(info => {
        helpfulInfoCounts[info] = (helpfulInfoCounts[info] || 0) + 1;
      });
    });

    const toChartData = (counts) => Object.entries(counts)
      .map(([name, value]) => ({ name, value }))
      .sort((a, b) => b.value - a.value);

    return {
      total: inquiries.length,
      status: toChartData(statusCounts),
      insurance: toChartData(insuranceCounts),
      coverage: toChartData(coverageCounts),
      metal: toChartData(metalCounts),
      knowsServices: toChartData(knowsServicesCounts),
      helpfulInfo: toChartData(helpfulInfoCounts),
      ward: toChartData(wardCounts),
      residence: toChartData(residenceCounts),
      unawareOfServices: knowsServicesCounts['No'] || 0,
      somewhatAware: knowsServicesCounts['Somewhat'] || 0
    };
  }, [inquiries]);

  const generatePDF = () => {
    if (!reportData) return;
    
    setIsGeneratingPDF(true);
    
    try {
      const pdf = new jsPDF('p', 'mm', 'a4');
      const pageWidth = pdf.internal.pageSize.getWidth();
      const pageHeight = pdf.internal.pageSize.getHeight();
      const margin = 15;
      let y = margin;

      // Helper functions
      const addText = (text, x, yPos, options = {}) => {
        pdf.setFontSize(options.size || 10);
        pdf.setFont('helvetica', options.style || 'normal');
        pdf.setTextColor(options.color || '#1e293b');
        pdf.text(text, x, yPos);
        return yPos + (options.lineHeight || 5);
      };

      const addSection = (title, yPos) => {
        if (yPos > pageHeight - 40) {
          pdf.addPage();
          yPos = margin;
        }
        pdf.setFillColor(20, 184, 166);
        pdf.rect(margin, yPos, pageWidth - 2*margin, 8, 'F');
        pdf.setFontSize(12);
        pdf.setFont('helvetica', 'bold');
        pdf.setTextColor(255, 255, 255);
        pdf.text(title, margin + 3, yPos + 5.5);
        return yPos + 12;
      };

      const addTable = (data, headers, yPos, colWidths) => {
        const rowHeight = 6;
        const tableWidth = colWidths.reduce((a, b) => a + b, 0);
        
        // Header
        pdf.setFillColor(241, 245, 249);
        pdf.rect(margin, yPos, tableWidth, rowHeight, 'F');
        pdf.setFontSize(9);
        pdf.setFont('helvetica', 'bold');
        pdf.setTextColor(71, 85, 105);
        
        let x = margin + 2;
        headers.forEach((header, i) => {
          pdf.text(header, x, yPos + 4);
          x += colWidths[i];
        });
        yPos += rowHeight;

        // Rows
        pdf.setFont('helvetica', 'normal');
        pdf.setTextColor(51, 65, 85);
        data.forEach((row, idx) => {
          if (yPos > pageHeight - 20) {
            pdf.addPage();
            yPos = margin;
          }
          
          if (idx % 2 === 0) {
            pdf.setFillColor(248, 250, 252);
            pdf.rect(margin, yPos, tableWidth, rowHeight, 'F');
          }
          
          x = margin + 2;
          row.forEach((cell, i) => {
            const cellText = String(cell).substring(0, 40);
            pdf.text(cellText, x, yPos + 4);
            x += colWidths[i];
          });
          yPos += rowHeight;
        });
        
        return yPos + 5;
      };

      // ===== PAGE 1: HEADER & EXECUTIVE SUMMARY =====
      
      // Header background
      pdf.setFillColor(13, 148, 136);
      pdf.rect(0, 0, pageWidth, 45, 'F');
      
      // Title
      pdf.setFontSize(22);
      pdf.setFont('helvetica', 'bold');
      pdf.setTextColor(255, 255, 255);
      pdf.text('HC4CC Inquiries Report', margin, 18);
      
      pdf.setFontSize(11);
      pdf.setFont('helvetica', 'normal');
      pdf.setTextColor(204, 251, 241);
      pdf.text('HealthCare4ChildCare Outreach & Engagement Analysis', margin, 26);
      pdf.text('DC Early Learning Collaborative', margin, 33);
      
      pdf.setFontSize(9);
      pdf.text(`Generated: ${new Date().toLocaleDateString('en-US', { weekday: 'long', year: 'numeric', month: 'long', day: 'numeric' })}`, margin, 40);
      
      y = 55;

      // Executive Summary
      y = addSection('EXECUTIVE SUMMARY', y);
      
      const summaryData = [
        ['Total Inquiries', reportData.total.toString()],
        ['Unaware of Free Services', `${reportData.unawareOfServices} (${Math.round(reportData.unawareOfServices/reportData.total*100)}%)`],
        ['Somewhat Aware', `${reportData.somewhatAware} (${Math.round(reportData.somewhatAware/reportData.total*100)}%)`],
        ['Total Needing Outreach', `${reportData.unawareOfServices + reportData.somewhatAware} (${Math.round((reportData.unawareOfServices + reportData.somewhatAware)/reportData.total*100)}%)`]
      ];
      
      y = addTable(summaryData, ['Metric', 'Value'], y, [90, 90]);

      // ===== QUESTIONS & NEEDS ANALYSIS =====
      y = addSection('QUESTIONS & NEEDS ANALYSIS', y);
      
      const needsData = reportData.helpfulInfo.map(item => [
        item.name,
        item.value.toString(),
        `${Math.round(item.value/reportData.total*100)}%`
      ]);
      y = addTable(needsData, ['Information Type', 'Count', '%'], y, [120, 30, 30]);

      // ===== INSURANCE COMPANIES =====
      y = addSection('INSURANCE COMPANIES', y);
      
      const insuranceData = reportData.insurance.map(item => [
        item.name,
        item.value.toString(),
        `${Math.round(item.value/reportData.total*100)}%`
      ]);
      y = addTable(insuranceData, ['Insurance Company', 'Count', '%'], y, [120, 30, 30]);

      // ===== COVERAGE TYPES =====
      y = addSection('COVERAGE TYPES', y);
      
      const coverageData = reportData.coverage.map(item => [
        item.name,
        item.value.toString(),
        `${Math.round(item.value/reportData.total*100)}%`
      ]);
      y = addTable(coverageData, ['Coverage Type', 'Count', '%'], y, [120, 30, 30]);

      // ===== METAL LEVELS =====
      y = addSection('PLAN METAL LEVELS', y);
      
      const metalData = reportData.metal.map(item => [
        item.name,
        item.value.toString(),
        `${Math.round(item.value/reportData.total*100)}%`
      ]);
      y = addTable(metalData, ['Metal Level', 'Count', '%'], y, [120, 30, 30]);

      // ===== AWARENESS OF FREE SERVICES =====
      y = addSection('AWARENESS OF FREE SERVICES', y);
      
      const awarenessData = reportData.knowsServices.map(item => [
        item.name,
        item.value.toString(),
        `${Math.round(item.value/reportData.total*100)}%`
      ]);
      y = addTable(awarenessData, ['Response', 'Count', '%'], y, [120, 30, 30]);

      // ===== WARD DISTRIBUTION =====
      y = addSection('WARD DISTRIBUTION', y);
      
      const wardData = reportData.ward.map(item => [
        item.name,
        item.value.toString(),
        `${Math.round(item.value/reportData.total*100)}%`
      ]);
      y = addTable(wardData, ['Ward', 'Count', '%'], y, [120, 30, 30]);

      // ===== RESIDENCE LOCATION =====
      y = addSection('RESIDENCE LOCATION', y);
      
      const residenceData = reportData.residence.map(item => [
        item.name,
        item.value.toString(),
        `${Math.round(item.value/reportData.total*100)}%`
      ]);
      y = addTable(residenceData, ['Location', 'Count', '%'], y, [120, 30, 30]);

      // ===== INQUIRY STATUS =====
      y = addSection('INQUIRY STATUS', y);
      
      const statusData = reportData.status.map(item => [
        item.name.replace('_', ' ').toUpperCase(),
        item.value.toString(),
        `${Math.round(item.value/reportData.total*100)}%`
      ]);
      y = addTable(statusData, ['Status', 'Count', '%'], y, [120, 30, 30]);

      // ===== KEY INSIGHTS =====
      if (y > pageHeight - 60) {
        pdf.addPage();
        y = margin;
      }
      
      y = addSection('KEY INSIGHTS & RECOMMENDATIONS', y);
      
      pdf.setFontSize(10);
      pdf.setFont('helvetica', 'normal');
      pdf.setTextColor(51, 65, 85);
      
      const insights = [
        `• Top Insurance Provider: ${reportData.insurance[0]?.name || 'N/A'} (${reportData.insurance[0]?.value || 0} workers)`,
        `• Most Requested Information: ${reportData.helpfulInfo[0]?.name || 'N/A'} (${reportData.helpfulInfo[0]?.value || 0} requests)`,
        `• Top Ward: ${reportData.ward[0]?.name || 'N/A'} (${reportData.ward[0]?.value || 0} inquiries)`,
        `• Outreach Opportunity: ${Math.round((reportData.unawareOfServices + reportData.somewhatAware)/reportData.total*100)}% of workers need education about free plan services`
      ];
      
      insights.forEach(insight => {
        if (y > pageHeight - 15) {
          pdf.addPage();
          y = margin;
        }
        pdf.text(insight, margin, y);
        y += 7;
      });

      // Footer
      y = pageHeight - 15;
      pdf.setFontSize(8);
      pdf.setTextColor(148, 163, 184);
      pdf.text('DC Early Learning Collaborative | HealthCare4ChildCare Outreach & Engagement Partnership | FY2026 Grantee', margin, y);
      pdf.text(`Report generated: ${new Date().toLocaleString()}`, margin, y + 4);

      // Save
      pdf.save(`HC4CC_Report_${new Date().toISOString().split('T')[0]}.pdf`);
      
    } catch (error) {
      console.error('Error generating PDF:', error);
      alert('Error generating PDF. Please try again.');
    } finally {
      setIsGeneratingPDF(false);
    }
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-50 overflow-y-auto bg-black/50 backdrop-blur-sm">
      <div className="min-h-screen px-4 py-8">
        {/* Control Bar */}
        <div className="sticky top-0 z-10 bg-white rounded-xl shadow-lg p-4 mb-4 max-w-6xl mx-auto flex items-center justify-between">
          <h2 className="text-lg font-bold text-slate-800">HC4CC Inquiries Visual Report</h2>
          <div className="flex gap-3">
            <Button 
              onClick={generatePDF} 
              disabled={isGeneratingPDF}
              className="bg-teal-500 hover:bg-teal-600 text-white"
            >
              {isGeneratingPDF ? (
                <>
                  <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                  Generating...
                </>
              ) : (
                <>
                  <Download className="w-4 h-4 mr-2" />
                  Download PDF
                </>
              )}
            </Button>
            <Button onClick={onClose} variant="outline">
              <X className="w-4 h-4 mr-2" />
              Close
            </Button>
          </div>
        </div>

        {/* Visual Report Content */}
        <div className="bg-white rounded-2xl max-w-6xl mx-auto shadow-xl">
          {/* Header */}
          <div className="bg-gradient-to-r from-teal-600 to-cyan-700 p-8 rounded-t-2xl">
            <div className="text-white text-2xl font-bold mb-1">DC Early Learning Collaborative</div>
            <h1 className="text-3xl font-bold text-white mb-2">HC4CC Inquiries Report</h1>
            <p className="text-teal-100">HealthCare4ChildCare Outreach & Engagement Analysis</p>
            <p className="text-teal-200 text-sm mt-2">
              Generated: {new Date().toLocaleDateString('en-US', { weekday: 'long', year: 'numeric', month: 'long', day: 'numeric' })}
            </p>
          </div>

          {!reportData ? (
            <div className="p-12 text-center">
              <AlertCircle className="w-12 h-12 text-slate-300 mx-auto mb-4" />
              <p className="text-slate-500">No inquiry data available.</p>
            </div>
          ) : (
            <div className="p-8 space-y-8">
              {/* Summary Stats */}
              <div>
                <h2 className="text-xl font-bold text-slate-800 mb-4 flex items-center gap-2">
                  <TrendingUp className="w-5 h-5 text-teal-600" />
                  Executive Summary
                </h2>
                <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                  <div className="bg-teal-50 rounded-xl p-5 border border-teal-200">
                    <div className="flex items-center gap-2 mb-2">
                      <Users className="w-5 h-5 text-teal-600" />
                      <span className="text-sm font-medium text-teal-700">Total Inquiries</span>
                    </div>
                    <p className="text-3xl font-bold text-teal-800">{reportData.total}</p>
                  </div>
                  <div className="bg-red-50 rounded-xl p-5 border border-red-200">
                    <div className="flex items-center gap-2 mb-2">
                      <AlertCircle className="w-5 h-5 text-red-600" />
                      <span className="text-sm font-medium text-red-700">Unaware of Services</span>
                    </div>
                    <p className="text-3xl font-bold text-red-800">{reportData.unawareOfServices}</p>
                    <p className="text-xs text-red-600">{reportData.total > 0 ? Math.round(reportData.unawareOfServices/reportData.total*100) : 0}% of total</p>
                  </div>
                  <div className="bg-amber-50 rounded-xl p-5 border border-amber-200">
                    <div className="flex items-center gap-2 mb-2">
                      <HelpCircle className="w-5 h-5 text-amber-600" />
                      <span className="text-sm font-medium text-amber-700">Somewhat Aware</span>
                    </div>
                    <p className="text-3xl font-bold text-amber-800">{reportData.somewhatAware}</p>
                    <p className="text-xs text-amber-600">{reportData.total > 0 ? Math.round(reportData.somewhatAware/reportData.total*100) : 0}% of total</p>
                  </div>
                  <div className="bg-emerald-50 rounded-xl p-5 border border-emerald-200">
                    <div className="flex items-center gap-2 mb-2">
                      <TrendingUp className="w-5 h-5 text-emerald-600" />
                      <span className="text-sm font-medium text-emerald-700">Outreach Needed</span>
                    </div>
                    <p className="text-3xl font-bold text-emerald-800">{reportData.unawareOfServices + reportData.somewhatAware}</p>
                    <p className="text-xs text-emerald-600">{reportData.total > 0 ? Math.round((reportData.unawareOfServices + reportData.somewhatAware)/reportData.total*100) : 0}% opportunity</p>
                  </div>
                </div>
              </div>

              {/* Information Needs */}
              <div className="bg-violet-50 rounded-xl p-6 border border-violet-200">
                <h2 className="text-xl font-bold text-slate-800 mb-2 flex items-center gap-2">
                  <HelpCircle className="w-5 h-5 text-violet-600" />
                  Questions & Needs Analysis
                </h2>
                <p className="text-sm text-slate-600 mb-4">What information child care workers need most</p>
                <div className="h-64">
                  <ResponsiveContainer width="100%" height="100%">
                    <BarChart data={reportData.helpfulInfo} layout="vertical" margin={{ left: 10, right: 30 }}>
                      <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" />
                      <XAxis type="number" stroke="#64748b" />
                      <YAxis type="category" dataKey="name" width={200} tick={{ fontSize: 11 }} stroke="#64748b" />
                      <Tooltip formatter={(value) => [`${value} responses`, 'Count']} />
                      <Bar dataKey="value" fill="#8b5cf6" radius={[0, 6, 6, 0]} />
                    </BarChart>
                  </ResponsiveContainer>
                </div>
              </div>

              {/* Two Column Charts */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                {/* Insurance Companies */}
                <div className="bg-white rounded-xl p-6 border border-slate-200">
                  <h3 className="text-lg font-bold text-slate-800 mb-4 flex items-center gap-2">
                    <Shield className="w-5 h-5 text-teal-600" />
                    Insurance Companies
                  </h3>
                  <div className="h-48">
                    <ResponsiveContainer width="100%" height="100%">
                      <PieChart>
                        <Pie data={reportData.insurance} cx="50%" cy="50%" outerRadius={70} dataKey="value" label={({ name, percent }) => `${name} ${(percent*100).toFixed(0)}%`}>
                          {reportData.insurance.map((entry, index) => (
                            <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
                          ))}
                        </Pie>
                        <Tooltip />
                      </PieChart>
                    </ResponsiveContainer>
                  </div>
                </div>

                {/* Coverage Types */}
                <div className="bg-white rounded-xl p-6 border border-slate-200">
                  <h3 className="text-lg font-bold text-slate-800 mb-4 flex items-center gap-2">
                    <FileText className="w-5 h-5 text-cyan-600" />
                    Coverage Types
                  </h3>
                  <div className="h-48">
                    <ResponsiveContainer width="100%" height="100%">
                      <BarChart data={reportData.coverage}>
                        <CartesianGrid strokeDasharray="3 3" />
                        <XAxis dataKey="name" tick={{ fontSize: 10 }} />
                        <YAxis />
                        <Tooltip />
                        <Bar dataKey="value" fill="#0891b2" radius={[6, 6, 0, 0]} />
                      </BarChart>
                    </ResponsiveContainer>
                  </div>
                </div>

                {/* Awareness of Free Services */}
                <div className="bg-white rounded-xl p-6 border border-slate-200">
                  <h3 className="text-lg font-bold text-slate-800 mb-4">Awareness of Free Services</h3>
                  <div className="h-48">
                    <ResponsiveContainer width="100%" height="100%">
                      <PieChart>
                        <Pie data={reportData.knowsServices} cx="50%" cy="50%" innerRadius={40} outerRadius={70} dataKey="value" label={({ name, percent }) => `${name} ${(percent*100).toFixed(0)}%`}>
                          {reportData.knowsServices.map((entry, index) => {
                            const colors = { 'Yes': '#10b981', 'No': '#ef4444', 'Somewhat': '#f59e0b' };
                            return <Cell key={`cell-${index}`} fill={colors[entry.name] || COLORS[index]} />;
                          })}
                        </Pie>
                        <Tooltip />
                      </PieChart>
                    </ResponsiveContainer>
                  </div>
                </div>

                {/* Ward Distribution */}
                <div className="bg-white rounded-xl p-6 border border-slate-200">
                  <h3 className="text-lg font-bold text-slate-800 mb-4 flex items-center gap-2">
                    <MapPin className="w-5 h-5 text-indigo-600" />
                    Ward Distribution
                  </h3>
                  <div className="h-48">
                    <ResponsiveContainer width="100%" height="100%">
                      <BarChart data={reportData.ward}>
                        <CartesianGrid strokeDasharray="3 3" />
                        <XAxis dataKey="name" tick={{ fontSize: 10 }} />
                        <YAxis />
                        <Tooltip />
                        <Bar dataKey="value" fill="#6366f1" radius={[6, 6, 0, 0]} />
                      </BarChart>
                    </ResponsiveContainer>
                  </div>
                </div>
              </div>

              {/* Key Insights */}
              <div className="bg-slate-800 rounded-xl p-6 text-white">
                <h3 className="text-lg font-bold mb-4">Key Insights & Recommendations</h3>
                <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-4">
                  <div className="bg-white/10 rounded-lg p-4">
                    <p className="text-slate-400 text-xs mb-1">Top Insurance Provider</p>
                    <p className="text-lg font-semibold">{reportData.insurance[0]?.name || 'N/A'}</p>
                    <p className="text-teal-400 text-sm">{reportData.insurance[0]?.value || 0} workers</p>
                  </div>
                  <div className="bg-white/10 rounded-lg p-4">
                    <p className="text-slate-400 text-xs mb-1">Most Requested Info</p>
                    <p className="text-lg font-semibold">{reportData.helpfulInfo[0]?.name?.substring(0, 20) || 'N/A'}...</p>
                    <p className="text-violet-400 text-sm">{reportData.helpfulInfo[0]?.value || 0} requests</p>
                  </div>
                  <div className="bg-white/10 rounded-lg p-4">
                    <p className="text-slate-400 text-xs mb-1">Top Ward</p>
                    <p className="text-lg font-semibold">{reportData.ward[0]?.name || 'N/A'}</p>
                    <p className="text-indigo-400 text-sm">{reportData.ward[0]?.value || 0} inquiries</p>
                  </div>
                </div>
                <div className="bg-amber-500/20 rounded-lg p-4 border border-amber-500/30">
                  <p className="text-amber-300 font-semibold text-sm">📊 Outreach Opportunity</p>
                  <p className="text-slate-300 text-sm mt-1">
                    {reportData.total > 0 ? Math.round((reportData.unawareOfServices + reportData.somewhatAware)/reportData.total*100) : 0}% of respondents 
                    ({reportData.unawareOfServices + reportData.somewhatAware} workers) need education about free services.
                  </p>
                </div>
              </div>

              {/* Footer */}
              <div className="text-center text-slate-500 text-xs pt-4 border-t border-slate-200">
                <p className="font-medium">DC Early Learning Collaborative</p>
                <p>HealthCare4ChildCare Outreach & Engagement Partnership • FY2026 Grantee</p>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default HC4CCReportModal;
